/**
 * IdentitesPrestatairesPage — Administration → Prestataires externes (socle S3).
 *
 * Gère les identités de document des prestataires qui exploitent la cantine, le
 * périscolaire ou la petite enfance à la place de la mairie. Les documents de
 * ces briques portent alors l'en-tête, le SIRET et les mentions du prestataire.
 * Sans rattachement, la brique retombe sur la charte mairie.
 *
 * Réservé admin complet (permission admin.identite_prestataire.manage).
 * Aucun window.confirm : useConfirmDialog (invariant VPC).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Button, Input, Field, Alert, Badge, SectionEyebrow,
} from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { DocumentIdentite, DocumentIdentiteInput, BriqueDeleguable, BriqueDeleguableMeta } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import {
  Building2, Upload, Trash2, Plus, Pencil, AlertCircle, CheckCircle2, Link2, X,
} from 'lucide-react';

const EMPTY: DocumentIdentiteInput = {
  nom: '', adresse: '', codePostal: '', ville: '',
  telephone: '', emailContact: '', siret: '', mentionLegale: '', piedTexte: '',
};

export function IdentitesPrestatairesPage() {
  const [list, setList] = useState<DocumentIdentite[]>([]);
  const [briques, setBriques] = useState<BriqueDeleguableMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState<DocumentIdentite | 'new' | null>(null);
  const { confirm, dialogElement } = useConfirmDialog();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [l, b] = await Promise.all([
        api.admin.tenant.identitesPrestataires.list(),
        api.admin.tenant.identitesPrestataires.briques(),
      ]);
      setList(l); setBriques(b);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  function flash(msg: string) { setSuccess(msg); setTimeout(() => setSuccess(null), 2500); }

  // Rattachement d'une brique à une identité (ou détachement).
  async function rattacher(brique: BriqueDeleguable, identiteId: string | null) {
    setError(null);
    try {
      await api.admin.tenant.identitesPrestataires.setRattachement(brique, identiteId);
      await load();
      flash(identiteId ? 'Prestataire rattaché.' : 'Retour à la charte mairie.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Rattachement impossible'); }
  }

  async function toggleActif(it: DocumentIdentite) {
    if (it.actif) {
      const ok = await confirm({
        title: `Désactiver « ${it.nom} » ?`,
        description: 'Le prestataire ne sera plus proposé au rattachement. Les rattachements existants sont conservés mais n\'imprimeront plus son identité.',
        confirmLabel: 'Désactiver', danger: true,
      });
      if (!ok) return;
    }
    try {
      await api.admin.tenant.identitesPrestataires.setActif(it.id, !it.actif);
      await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Action impossible'); }
  }

  // Quelle identité est rattachée à une brique donnée ?
  function identitePourBrique(brique: BriqueDeleguable): DocumentIdentite | null {
    return list.find((it) => it.briques.includes(brique)) ?? null;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <SectionEyebrow>Administration</SectionEyebrow>
      <div className="mb-1 flex items-center gap-2">
        <h1 className="font-display text-xl font-semibold text-ink">Prestataires externes</h1>
      </div>
      <p className="mb-5 max-w-2xl text-sm text-ink-muted">
        Quand la cantine, le périscolaire ou la petite enfance sont gérés par un
        prestataire (une agglomération, une association…), ses documents portent
        son en-tête et son SIRET. Sans rattachement, la brique utilise la charte
        de la mairie.
      </p>

      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" icon={CheckCircle2} className="mb-3">{success}</Alert>}

      {/* Rattachement par brique */}
      <Card variant="glass" padded className="mb-5">
        <div className="mb-3 flex items-center gap-2">
          <Link2 className="h-4 w-4 text-grc-fg" />
          <h2 className="font-display text-base font-semibold text-ink">Qui édite les documents de chaque service ?</h2>
        </div>
        <div className="space-y-2">
          {briques.map((b) => {
            const courant = identitePourBrique(b.cle);
            return (
              <div key={b.cle} className="flex flex-wrap items-center justify-between gap-2 rounded-r-lg border border-border bg-surface/40 px-3 py-2">
                <div className="text-sm font-medium text-ink">{b.libelle}</div>
                <div className="flex items-center gap-2">
                  <select
                    className="rounded-r-md border border-border bg-surface px-2 py-1 text-sm text-ink"
                    value={courant?.id ?? ''}
                    onChange={(e) => void rattacher(b.cle, e.target.value || null)}
                  >
                    <option value="">Charte mairie (par défaut)</option>
                    {list.filter((it) => it.actif || it.id === courant?.id).map((it) => (
                      <option key={it.id} value={it.id}>{it.nom}</option>
                    ))}
                  </select>
                  {courant
                    ? <Badge variant="grc">Prestataire</Badge>
                    : <Badge variant="neutral">Mairie</Badge>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Liste des prestataires */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-base font-semibold text-ink">Identités enregistrées</h2>
        <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Nouveau prestataire
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : list.length === 0 ? (
        <Card padded><p className="text-sm text-ink-muted">Aucun prestataire enregistré. La cantine, le périscolaire et la petite enfance utilisent la charte de la mairie.</p></Card>
      ) : (
        <div className="space-y-2">
          {list.map((it) => (
            <Card key={it.id} padded className={it.actif ? '' : 'opacity-60'}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 flex-none items-center justify-center overflow-hidden rounded-r-lg border border-border bg-surface/50">
                    {it.logoUrl
                      ? <img src={it.logoUrl} alt="" className="h-full w-full object-contain" />
                      : <Building2 className="h-5 w-5 text-ink-dim" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink">{it.nom}</span>
                      {!it.actif && <Badge variant="neutral" size="sm">Désactivé</Badge>}
                    </div>
                    <div className="text-2xs text-ink-subtle">
                      {[it.siret ? `SIRET ${it.siret}` : null, it.ville, it.emailContact].filter(Boolean).join(' · ') || 'Coordonnées non renseignées'}
                    </div>
                    {it.briques.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {it.briques.map((br) => (
                          <Badge key={br} variant="grc" size="sm">{briques.find((b) => b.cle === br)?.libelle ?? br}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(it)}><Pencil className="h-4 w-4" /> Modifier</Button>
                  <Button variant="ghost" size="sm" onClick={() => void toggleActif(it)} className="text-ink-muted">
                    {it.actif ? 'Désactiver' : 'Réactiver'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <PrestataireForm
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); flash(msg); void load(); }}
          onError={setError}
        />
      )}

      {dialogElement}
    </div>
  );
}

// ── Formulaire création / édition ───────────────────────────────────────────
function PrestataireForm({ initial, onClose, onSaved, onError }: {
  initial: DocumentIdentite | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [form, setForm] = useState<DocumentIdentiteInput>(initial
    ? {
        nom: initial.nom, adresse: initial.adresse ?? '', codePostal: initial.codePostal ?? '',
        ville: initial.ville ?? '', telephone: initial.telephone ?? '', emailContact: initial.emailContact ?? '',
        siret: initial.siret ?? '', mentionLegale: initial.mentionLegale ?? '', piedTexte: initial.piedTexte ?? '',
      }
    : { ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState<DocumentIdentite | null>(initial);
  const logoRef = useRef<HTMLInputElement>(null);

  function set<K extends keyof DocumentIdentiteInput>(k: K, v: DocumentIdentiteInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    if (!form.nom.trim()) { onError('Le nom du prestataire est obligatoire.'); return; }
    setBusy(true);
    try {
      const saved = current
        ? await api.admin.tenant.identitesPrestataires.update(current.id, form)
        : await api.admin.tenant.identitesPrestataires.create(form);
      onSaved(current ? 'Prestataire modifié.' : 'Prestataire créé.');
      return saved;
    } catch (e) { onError(e instanceof ApiException ? e.message : 'Enregistrement impossible'); }
    finally { setBusy(false); }
  }

  async function uploadLogo(file: File) {
    if (!current) { onError('Enregistrez d\'abord le prestataire, puis ajoutez son logo.'); return; }
    setBusy(true);
    try {
      const compressed = await compressImageIfNeeded(file);
      const res = await api.admin.tenant.identitesPrestataires.uploadLogo(current.id, compressed, file.name);
      setCurrent(res);
    } catch (e) { onError(e instanceof ApiException ? e.message : 'Envoi impossible (PNG/JPEG).'); }
    finally { setBusy(false); if (logoRef.current) logoRef.current.value = ''; }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <Card variant="solid" padded className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-ink">{current ? 'Modifier le prestataire' : 'Nouveau prestataire'}</h3>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Fermer"><X className="h-4 w-4" aria-hidden="true" /></Button>
        </div>

        <div className="space-y-3">
          <Field label="Nom du prestataire" htmlFor="presta-nom" required>
            <Input id="presta-nom" value={form.nom} onChange={(e) => set('nom', e.target.value)} placeholder="Communauté d'Agglomération Rochefort Océan" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SIRET" htmlFor="presta-siret"><Input id="presta-siret" value={form.siret ?? ''} onChange={(e) => set('siret', e.target.value)} placeholder="200 040 563 00018" /></Field>
            <Field label="Téléphone" htmlFor="presta-tel"><Input id="presta-tel" value={form.telephone ?? ''} onChange={(e) => set('telephone', e.target.value)} /></Field>
          </div>
          <Field label="Adresse" htmlFor="presta-adr"><Input id="presta-adr" value={form.adresse ?? ''} onChange={(e) => set('adresse', e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code postal" htmlFor="presta-cp"><Input id="presta-cp" value={form.codePostal ?? ''} onChange={(e) => set('codePostal', e.target.value)} /></Field>
            <Field label="Ville" htmlFor="presta-ville"><Input id="presta-ville" value={form.ville ?? ''} onChange={(e) => set('ville', e.target.value)} /></Field>
          </div>
          <Field label="Email de contact" htmlFor="presta-email"><Input id="presta-email" value={form.emailContact ?? ''} onChange={(e) => set('emailContact', e.target.value)} /></Field>
          <Field label="Mention légale (pied court)" htmlFor="presta-mention" hint="Ex. « EPCI — SIRET 200 040 563 00018 ». Apparaît en pied des documents.">
            <Input id="presta-mention" value={form.mentionLegale ?? ''} onChange={(e) => set('mentionLegale', e.target.value)} />
          </Field>
          <Field label="Pied de document (texte long)" htmlFor="presta-pied" hint="Prioritaire sur les coordonnées si renseigné.">
            <textarea
              id="presta-pied"
              className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink"
              rows={2} value={form.piedTexte ?? ''} onChange={(e) => set('piedTexte', e.target.value)}
            />
          </Field>

          {/* Logo : possible seulement après création (besoin de l'id). */}
          <div className="border-t border-border pt-3">
            <div className="mb-1.5 text-sm font-medium text-ink">Logo du prestataire</div>
            {!current ? (
              <p className="text-2xs text-ink-subtle">Enregistrez d’abord, puis vous pourrez ajouter le logo.</p>
            ) : (
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 flex-none items-center justify-center overflow-hidden rounded-r-lg border border-border bg-surface/50">
                  {current.logoUrl
                    ? <img src={current.logoUrl} alt="" className="h-full w-full object-contain" />
                    : <Building2 className="h-6 w-6 text-ink-dim" />}
                </div>
                <input ref={logoRef} type="file" accept="image/png,image/jpeg" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadLogo(f); }} />
                <Button variant="secondary" size="sm" onClick={() => logoRef.current?.click()} disabled={busy}>
                  <Upload className="h-4 w-4" /> {current.logoUrl ? 'Remplacer' : 'Téléverser'}
                </Button>
                {current.logoUrl && (
                  <Button variant="ghost" size="sm" disabled={busy} className="text-ink-muted hover:text-urgent"
                    onClick={async () => { try { const r = await api.admin.tenant.identitesPrestataires.removeLogo(current.id); setCurrent(r); } catch { /* ignore */ } }}>
                    <Trash2 className="h-4 w-4" /> Retirer
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Fermer</Button>
          <Button variant="primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Enregistrement…' : (current ? 'Enregistrer' : 'Créer')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
